// Copyright 2018 Sorama B.V.

import { autoinject, BindingEngine, Container, Disposable } from "aurelia-framework";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { ClientStatus, IClientService } from "services/Client/IClientService";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IMeasurementStorageService } from "services/Measurement/IMeasurementStorageService";
import { CloudSoundProcessingService } from "services/SoundProcessing/CloudSoundProcessingService";
import { IBeamformingProcessingService } from "services/SoundProcessing/IBeamformingProcessingService";
import { IHologramProcessingService } from "services/SoundProcessing/IHologramProcessingService";
import { IHolographyProcessingService } from "services/SoundProcessing/IHolographyProcessingService";
import { IPsychoAcousticsProcessingService } from "services/SoundProcessing/IPsychoAcousticsProcessingService";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";
import { LocalSoundProcessingService } from "services/SoundProcessing/LocalSoundProcessingService";
import { IStorageService } from "services/Storage/IStorageService";

import { CloudVideoFileService } from "services/Storage/CloudVideoFileService";
import { IMeasurementVideoFileService } from "services/Storage/IMeasurementVideoFileService";
import { LocalVideoFileService } from "services/Storage/LocalVideoFileService";

@autoinject
export class MeasurementProcessorResolver implements IMeasurementProcessorResolver {
    private static readonly notAvailableErrorMessage: string =
        "Cannot process this measurement, the measurement data is not available";

    private readonly container: Container;
    private readonly bindingEngine: BindingEngine;
    private readonly clientService: IClientService;
    private readonly cloudStorageService: IMeasurementStorageService;
    private readonly localStorageService: IStorageService;

    private serviceLookup: { service: unknown; key: string }[];
    private statusChangedCallbacks: ((measurementId: string) => void)[] = [];

    private processCache: { [key: string]: { cloud: boolean; local: boolean } } = {};
    private cacheUpdatePromise: Promise<unknown> = Promise.resolve();

    constructor(
        container: Container,
        bindingEngine: BindingEngine,
        clientService: IClientService,
        localStorageService: IStorageService,
        cloudStorageService: IMeasurementStorageService
    ) {
        this.container = container;
        this.bindingEngine = bindingEngine;
        this.localStorageService = localStorageService;
        this.cloudStorageService = cloudStorageService;
        this.clientService = clientService;

        this.serviceLookup = [
            { service: ISpectralProcessingService, key: "ISpectralProcessingService" },
            { service: IHologramProcessingService, key: "IHologramProcessingService" },
            { service: IBeamformingProcessingService, key: "IBeamformingProcessingService" },
            { service: IHolographyProcessingService, key: "IHolographyProcessingService" },
            { service: IMeasurementVideoFileService, key: "IMeasurementVideoFileService" },
            { service: IPsychoAcousticsProcessingService, key: "IPsychoAcousticsProcessingService" },
        ];

        // Register sub services

        // Register the local service if available for that interface
        this.container.registerSingleton(LocalSoundProcessingService, LocalSoundProcessingService);
        this.container.registerAlias(LocalSoundProcessingService, this.serviceLookup[0].key + "Local");
        this.container.registerAlias(LocalSoundProcessingService, this.serviceLookup[1].key + "Local");
        this.container.registerAlias(LocalSoundProcessingService, this.serviceLookup[2].key + "Local");
        this.container.registerAlias(LocalSoundProcessingService, this.serviceLookup[5].key + "Local");

        // Register the cloud service if available for that interface
        this.container.registerSingleton(CloudSoundProcessingService, CloudSoundProcessingService);
        this.container.registerAlias(CloudSoundProcessingService, this.serviceLookup[0].key + "Cloud");
        this.container.registerAlias(CloudSoundProcessingService, this.serviceLookup[2].key + "Cloud");
        this.container.registerAlias(CloudSoundProcessingService, this.serviceLookup[3].key + "Cloud");
        this.container.registerAlias(CloudSoundProcessingService, this.serviceLookup[5].key + "Cloud");

        this.container.registerSingleton(this.serviceLookup[4].key + "Local", LocalVideoFileService);
        this.container.registerSingleton(this.serviceLookup[4].key + "Cloud", CloudVideoFileService);

        // Register on the client status changed event
        this.bindingEngine.propertyObserver(this.clientService, "status").subscribe(this.clientStatusChanged);
        this.bindingEngine.propertyObserver(this.clientService, "storagePath").subscribe(this.clientStoragePathChanged);
    }

    public canProcess(
        measurements: GenericMeasurement[],
        interfaceKey?: string
    ): Promise<{ measurement: GenericMeasurement; available: boolean }[]> {
        // First determine which measurements are computed measurements, we can skip these measurements and use the measurement id of the source measurement
        const addionalMeasurements = <GenericMeasurement[]>[];
        const requestMeasurements = measurements.filter((measurement) => {
            if (this.processCache[measurement.id]) {
                // Already have this status
                return false;
            } else if (measurement.isComputedMeasurement) {
                // Make sure the computed measurement is also in the list
                if (
                    !measurements.some((x) => x.id === measurement.computedMeasurementSourceId) &&
                    measurement.computedMeasurementSource
                ) {
                    if (!this.processCache[measurement.computedMeasurementSourceId]) {
                        addionalMeasurements.push(measurement.computedMeasurementSource);
                    }
                }
                return false;
            }

            return true;
        });

        requestMeasurements.splice(0, 0, ...addionalMeasurements);

        this.cacheUpdatePromise = this.cacheUpdatePromise.then(() => {
            // Get all storage info from the cloud and local
            const cloudPromiseFetch =
                requestMeasurements.length === 0
                    ? Promise.resolve<{ measurementId: string; available: boolean }[]>([])
                    : this.cloudStorageService.areMeasurementsAvailable(requestMeasurements.map((x) => x.id));

            const cloudPromise = cloudPromiseFetch
                .then((stati) => {
                    // Make sure that for each of the requested measurements there is an entry present
                    return measurements.map((measurement) => {
                        const testId = measurement.isComputedMeasurement
                            ? measurement.computedMeasurementSourceId
                            : measurement.id;
                        let status = stati.find((x) => x.measurementId === testId);

                        if (status === undefined) {
                            // Try the status we have available
                            const availableStatus = this.processCache[testId];
                            if (availableStatus) {
                                status = { measurementId: testId, available: availableStatus.cloud };
                            }
                        }

                        if (
                            !measurement.isComputedMeasurement &&
                            requestMeasurements.findIndex((x) => x.id === measurement.id) === -1
                        ) {
                            // Measurement was not requested, ignore value
                            return undefined;
                        }

                        return status
                            ? { measurement: measurement, available: status.available }
                            : { measurement: measurement, available: false };
                    });
                })
                .catch(() => undefined);

            const localPromiseFetch =
                requestMeasurements.length === 0
                    ? Promise.resolve<{ [key: string]: boolean }>({})
                    : this.localStorageService.areMeasurementsAvailable(requestMeasurements);

            const localPromise = localPromiseFetch
                .then((stati) => {
                    // Make sure that for each of the requested measurements there is an entry present
                    return measurements.map((measurement) => {
                        const testId = measurement.isComputedMeasurement
                            ? measurement.computedMeasurementSourceId
                            : measurement.id;
                        let status = stati[testId];

                        if (status === undefined) {
                            // Try the status we have available
                            const availableStatus = this.processCache[testId];
                            if (availableStatus) {
                                status = availableStatus.local;
                            }
                        }

                        if (
                            !measurement.isComputedMeasurement &&
                            requestMeasurements.findIndex((x) => x.id === measurement.id) === -1
                        ) {
                            // Measurement was not requested, ignore value
                            return undefined;
                        }

                        return { measurement: measurement, available: !!status };
                    });
                })
                .catch(() => undefined);

            // Await all promises
            return (this.cacheUpdatePromise = Promise.all([cloudPromise, localPromise])
                .then((availabilitySets) => {
                    const cloudAvailability = availabilitySets[0];
                    const localAvailability = availabilitySets[1];

                    // Update availability cache for cloud options
                    if (cloudAvailability) {
                        for (const availability of cloudAvailability) {
                            if (!availability) continue;

                            const id = availability.measurement.id;

                            if (!this.processCache[id]) {
                                this.processCache[id] = { cloud: availability.available, local: false };
                            } else {
                                this.processCache[id].cloud = availability.available;
                            }
                        }
                    }

                    // Update availability cache for local options
                    if (localAvailability) {
                        for (const availability of localAvailability) {
                            if (!availability) continue;

                            const id = availability.measurement.id;

                            if (!this.processCache[id]) {
                                this.processCache[id] = { local: availability.available, cloud: false };
                            } else {
                                this.processCache[id].local = availability.available;
                            }
                        }
                    }
                })
                .catch((e) => {
                    console.error(e);
                })
                .then(() => {
                    let isLocalAvailable = true;
                    let isCloudAvailable = true;

                    if (interfaceKey) {
                        // Check if the processor is unavailable for cloud or local
                        const key = this.getInterfaceKey(interfaceKey);

                        // Try to resolve both processors and see if theyre available
                        isLocalAvailable = this.container.get(key + "Local") !== key + "Local";
                        isCloudAvailable = this.container.get(key + "Cloud") !== key + "Cloud";
                    }

                    return measurements.map((x) => {
                        const cacheValue = this.processCache[x.id];
                        return {
                            measurement: x,
                            available:
                                cacheValue &&
                                ((isCloudAvailable && cacheValue.cloud) || (isLocalAvailable && cacheValue.local)),
                        };
                    });
                }));
        });

        return this.cacheUpdatePromise as Promise<{ measurement: GenericMeasurement; available: boolean }[]>;
    }

    public resolveProcessor<TProcessor>(interfaceKey: unknown, measurement: GenericMeasurement): Promise<TProcessor> {
        // Check if the measurement is available in the cache
        const cache = this.processCache[measurement.id];
        const key = this.getInterfaceKey(interfaceKey);

        const resolveAction: (c: { cloud: boolean; local: boolean }) => TProcessor = (c) => {
            let resolved: unknown;

            if (
                c.local &&
                (this.clientService.status === ClientStatus.Available ||
                    this.clientService.status === ClientStatus.OutdatedVersion)
            ) {
                const localKey = key + "Local";
                resolved = this.container.get(localKey);
                resolved = resolved === localKey ? undefined : resolved;
            }

            if (resolved) {
                return <TProcessor>resolved;
            }

            if (c.cloud) {
                const cloudKey = key + "Cloud";
                resolved = this.container.get(cloudKey);
                resolved = resolved === cloudKey ? undefined : resolved;
            }

            return <TProcessor>resolved;
        };

        if (cache) {
            // Measurement status is cached, use that
            const processor = resolveAction(cache);
            return processor
                ? Promise.resolve(processor)
                : Promise.reject(new Error(MeasurementProcessorResolver.notAvailableErrorMessage));
        } else {
            // Update cache, then do the rest
            return this.canProcess([measurement]).then((x) => {
                const processor =
                    x.length !== 0 && x[0].available ? resolveAction(this.processCache[measurement.id]) : undefined;

                if (!processor) throw new Error(MeasurementProcessorResolver.notAvailableErrorMessage);

                return processor;
            });
        }
    }

    public purgeProcessorStatus(measurementId: string): Promise<void> {
        const promise = this.cacheUpdatePromise ? this.cacheUpdatePromise : Promise.resolve();
        return (this.cacheUpdatePromise = promise
            .then(() => {
                if (this.processCache[measurementId]) {
                    delete this.processCache[measurementId];

                    this.statusChangedCallbacks.forEach((x) => x(measurementId));
                }
            })
            .catch(() => {}));
    }

    public registerProcessorStatusListener(statusChangedCallback: (measurementId: string) => void): Disposable {
        this.statusChangedCallbacks.push(statusChangedCallback);

        return {
            dispose: () => {
                if (this.statusChangedCallbacks) {
                    const index = this.statusChangedCallbacks.findIndex((x) => x === statusChangedCallback);
                    if (index === -1) {
                        return;
                    }
                    this.statusChangedCallbacks.splice(index, 1);
                }
            },
        };
    }

    private clientStatusChanged = (newStatus: ClientStatus, oldStatus: ClientStatus): void => {
        const newAvailable = newStatus === ClientStatus.Available || newStatus === ClientStatus.OutdatedVersion;
        const oldAvailable = oldStatus === ClientStatus.Available || oldStatus === ClientStatus.OutdatedVersion;

        if (newAvailable !== oldAvailable) {
            // Acquisition service has come online, we dont have a reference to the measurement (which we require to check local availability) flush the entire cache
            this.processCache = {};
        }
    };

    private clientStoragePathChanged = (newValue: string, oldValue: string): void => {
        if (newValue === oldValue || oldValue === undefined) return;

        // Clear cache
        this.processCache = {};
    };
    private getInterfaceKey(interfaceKey: unknown): string {
        let key: string;
        for (let i = 0; i < this.serviceLookup.length; i++) {
            if (this.serviceLookup[i].service == interfaceKey) {
                key = this.serviceLookup[i].key;
            }
        }

        return key;
    }
}
